import React, { useState, useEffect, useMemo } from "react";
import api from "../../api";
import jwt_decode from "jwt-decode";

// Helper: Compute level from totalexp
function getLevel(exp) {
  const lvl = Math.floor(Math.pow(exp, 1 / 3));
  return Math.pow(lvl + 1, 3) <= exp ? lvl + 1 : lvl;
}

const AuctionHouseModal = ({ isOpen, toggle, playerPos, spawnText }) => {
  const [activeTab, setActiveTab] = useState("available");
  const [availableAuctions, setAvailableAuctions] = useState([]);
  const [heldAuctions, setHeldAuctions] = useState([]);
  const [myBids, setMyBids] = useState([]);
  const [myPokemon, setMyPokemon] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(Date.now() / 1000);
  
  // Form state for holding an auction
  const [holdAuctionData, setHoldAuctionData] = useState({
    pokid: "",
    startingbid: "",
    duration: "",
  });
  
  // Duration selector state
  const [durationHours, setDurationHours] = useState("");
  const [durationMinutes, setDurationMinutes] = useState("");
  
  // Search states
  const [searchAvailable, setSearchAvailable] = useState("");
  const [searchHeld, setSearchHeld] = useState("");
  const [searchBids, setSearchBids] = useState("");
  
  // Bid modal state
  const [bidModalOpen, setBidModalOpen] = useState(false);
  const [currentAuction, setCurrentAuction] = useState(null);
  const [bidAmount, setBidAmount] = useState("");

  const authUser = (() => {
    try {
      // Try to get username from JWT token first
      const token = localStorage.getItem("jwtToken");
      if (token) {
        const decoded = jwt_decode(token);
        if (decoded.sub || decoded.username) {
          return decoded.sub || decoded.username;
        }
      }
      
      // Fallback to authUser if token is invalid or not present
      const user = localStorage.getItem("authUser");
      return user ? JSON.parse(user).username || user : "";
    } catch (error) {
      console.error("Error getting username:", error);
      return localStorage.getItem("authUser") || "";
    }
  })();

  // Live countdown timer
  useEffect(() => {
    if (isOpen) {
      const timer = setInterval(() => {
        setCurrentTime(Date.now() / 1000);
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [isOpen]);

  // Fetch data when modal opens or tab changes
  useEffect(() => {
    if (isOpen) {
      if (activeTab === "available") {
        fetchAvailableAuctions();
      } else if (activeTab === "hold") {
        fetchMyPokemon();
      } else if (activeTab === "myHeld") {
        fetchHeldAuctions();
      } else if (activeTab === "myBids") {
        fetchMyBids();
      }
    }
  }, [isOpen, activeTab]);

  // Auto-refresh auction data every 10 seconds
  useEffect(() => {
    if (!isOpen) return;

    const refreshInterval = setInterval(() => {
      if (activeTab === "available") {
        fetchAvailableAuctions();
      } else if (activeTab === "myHeld") {
        fetchHeldAuctions();
      } else if (activeTab === "myBids") {
        fetchMyBids();
      }
    }, 10000); // Refresh every 10 seconds

    return () => clearInterval(refreshInterval);
  }, [isOpen, activeTab]);

  const fetchAvailableAuctions = async () => {
    try {
      setLoading(true);
      // Fetch all pages to get complete data
      let allAuctions = [];
      let page = 1;
      let hasMore = true;
      
      while (hasMore) {
        const res = await api.get(`/v1/auctionhouse?page=${page}&size=50`, { withCredentials: true });
        const auctions = res.data.data || res.data;
        
        if (auctions.length === 0) {
          hasMore = false;
        } else {
          allAuctions = [...allAuctions, ...auctions];
          page++;
          // If we got less than 50, we're on the last page
          if (auctions.length < 50) {
            hasMore = false;
          }
        }
      }
      
      setAvailableAuctions(allAuctions);
    } catch (error) {
      console.error("Error fetching available auctions", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchHeldAuctions = async () => {
    try {
      setLoading(true);
      // Fetch all pages to get complete data
      let allAuctions = [];
      let page = 1;
      let hasMore = true;
      
      while (hasMore) {
        const res = await api.get(`/v1/auctionhouse?page=${page}&size=50`, { withCredentials: true });
        const auctions = res.data.data || res.data;
        
        if (auctions.length === 0) {
          hasMore = false;
        } else {
          allAuctions = [...allAuctions, ...auctions];
          page++;
          // If we got less than 50, we're on the last page
          if (auctions.length < 50) {
            hasMore = false;
          }
        }
      }
      
      // Filter to only show auctions owned by current user
      const myAuctions = allAuctions.filter(auction => 
        auction.trainer && auction.trainer.trim().toLowerCase() === authUser.toLowerCase()
      );
      setHeldAuctions(myAuctions);
    } catch (error) {
      console.error("Error fetching held auctions", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyBids = async () => {
    try {
      setLoading(true);
      // Fetch all pages to get complete data
      let allAuctions = [];
      let page = 1;
      let hasMore = true;
      
      while (hasMore) {
        const res = await api.get(`/v1/auctionhouse?page=${page}&size=50`, { withCredentials: true });
        const auctions = res.data.data || res.data;
        
        if (auctions.length === 0) {
          hasMore = false;
        } else {
          allAuctions = [...allAuctions, ...auctions];
          page++;
          // If we got less than 50, we're on the last page
          if (auctions.length < 50) {
            hasMore = false;
          }
        }
      }
      
      // Filter to only show auctions where current user is the highest bidder
      const myBidAuctions = allAuctions.filter(auction => 
        auction.lastbidUsername && auction.lastbidUsername.trim().toLowerCase() === authUser.toLowerCase()
      );
      setMyBids(myBidAuctions);
    } catch (error) {
      console.error("Error fetching my bids", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyPokemon = async () => {
    try {
      setLoading(true);
      const res = await api.get("/v1/pokemon/all", { withCredentials: true });
      const allowedColors = ["Emerald", "Diamond", "Ruby", "Golden", "Shadow", "Sapphire"];
      const filtered = (res.data.data || []).filter(
        (poke) =>
          allowedColors.some((prefix) => poke.name.startsWith(prefix)) &&
          poke.slot === 0 &&
          poke.nft === 0 &&
          poke.loan === 0
      );
      setMyPokemon(filtered);
    } catch (error) {
      console.error("Error fetching user Pokémon", error);
    } finally {
      setLoading(false);
    }
  };

  const getTimeLeft = (ends) => {
    const diff = Math.max(0, Math.floor(ends - currentTime));
    const h = Math.floor(diff / 3600);
    const m = Math.floor((diff % 3600) / 60);
    const s = diff % 60;
    return `${h}h ${m}m ${s}s`;
  };

  const isAuctionExpired = (ends) => {
    const diff = Math.max(0, Math.floor(ends - currentTime));
    return diff === 0;
  };

  const formatNumber = (num) => {
    return Number(num).toLocaleString();
  };

  const submitBid = async () => {
    try {
      await api.post(
        "/v1/auctionhouse/bid",
        {
          auction_id: currentAuction.id,
          bid_amount: parseInt(bidAmount),
        },
        { withCredentials: true }
      );
      
      spawnText("Bid placed successfully!", playerPos.x, playerPos.y, "#00ff00");
      setBidModalOpen(false);
      setBidAmount("");
      fetchAvailableAuctions();
    } catch (error) {
      spawnText("Failed to place bid!", playerPos.x, playerPos.y, "#ff0000");
    }
  };

  const submitHoldAuction = async () => {
    if (!holdAuctionData.pokid || !holdAuctionData.startingbid || !holdAuctionData.duration) {
      spawnText("Please fill in all fields!", playerPos.x, playerPos.y, "#ff0000");
      return;
    }
    if (parseInt(holdAuctionData.duration) < 10) {
      spawnText("Duration must be at least 10 minutes!", playerPos.x, playerPos.y, "#ff0000");
      return;
    }
    if (parseInt(holdAuctionData.startingbid) > 50000) {
      spawnText("Starting bid cannot exceed 50,000!", playerPos.x, playerPos.y, "#ff0000");
      return;
    }
    
    try {
      await api.post("/v1/auctionhouse", holdAuctionData, { withCredentials: true });
      spawnText("Auction created successfully!", playerPos.x, playerPos.y, "#00ff00");
      setHoldAuctionData({ pokid: "", startingbid: "", duration: "" });
      setDurationHours("");
      setDurationMinutes("");
      fetchHeldAuctions();
    } catch (error) {
      spawnText("Failed to create auction!", playerPos.x, playerPos.y, "#ff0000");
    }
  };

  // Filter functions
  const filteredAvailable = useMemo(
    () => availableAuctions.filter((auction) =>
      auction.name.toLowerCase().includes(searchAvailable.toLowerCase())
    ),
    [availableAuctions, searchAvailable]
  );

  const filteredMyHeld = useMemo(
    () => heldAuctions.filter((auction) =>
      auction.name.toLowerCase().includes(searchHeld.toLowerCase())
    ),
    [heldAuctions, searchHeld]
  );

  const filteredMyBids = useMemo(
    () => myBids.filter((auction) =>
      auction.name.toLowerCase().includes(searchBids.toLowerCase())
    ),
    [myBids, searchBids]
  );

  if (!isOpen) return null;

  return (
    <div 
      onClick={toggle}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px 10px',
        boxSizing: 'border-box',
        zIndex: 1050
      }}
    >
      <div 
        className="auction-house-popup"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '95%',
          maxWidth: 900,
          maxHeight: '95vh',
          background: 'linear-gradient(to bottom, rgba(29, 34, 46, 0.95), rgba(41, 50, 65, 0.95))',
          color: '#fff',
          borderRadius: 12,
          backdropFilter: 'blur(10px)',
          border: '2px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          margin: '10px'
        }}
      >
        {/* Header */}
        <div style={{
          padding: '12px 16px',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'rgba(0, 0, 0, 0.2)',
          flexWrap: 'wrap',
          gap: '8px',
          minHeight: '60px'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            flex: '1 1 auto',
            minWidth: '200px'
          }}>
            <h3 style={{ 
              margin: 0, 
              fontSize: '18px', 
              fontWeight: 'bold',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}>Auction House</h3>
          </div>
          
          <button
            onClick={toggle}
            style={{
              background: 'rgba(255, 255, 255, 0.1)',
              border: 'none',
              color: '#fff',
              borderRadius: '4px',
              padding: '6px 12px',
              cursor: 'pointer',
              transition: 'all 0.2s',
              fontSize: '14px'
            }}
            onMouseOver={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
            onMouseOut={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.1)'}
          >
            Close
          </button>
        </div>

        {/* Content */}
        <div style={{
          padding: '16px',
          overflowY: 'auto',
          flex: 1
        }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <div className="spinner-border text-light" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p style={{ marginTop: '10px' }}>Loading auction data...</p>
            </div>
          ) : (
            <>
              {/* Tab Navigation */}
              <div style={{
                display: 'flex',
                marginBottom: '20px',
                borderBottom: '2px solid rgba(255, 255, 255, 0.1)',
                overflowX: 'auto'
              }}>
                {[
                  { key: "available", label: "Available Auctions" },
                  { key: "hold", label: "Hold Auction" },
                  { key: "myHeld", label: "My Auctions" },
                  { key: "myBids", label: "My Bids" }
                ].map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    style={{
                      background: activeTab === tab.key ? 'rgba(255, 224, 102, 0.2)' : 'transparent',
                      border: 'none',
                      color: activeTab === tab.key ? '#ffe066' : '#ccc',
                      padding: '12px 20px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: activeTab === tab.key ? 'bold' : 'normal',
                      borderBottom: activeTab === tab.key ? '3px solid #ffe066' : '3px solid transparent',
                      transition: 'all 0.2s ease',
                      whiteSpace: 'nowrap',
                      marginRight: '8px'
                    }}
                    onMouseOver={(e) => {
                      if (activeTab !== tab.key) {
                        e.target.style.background = 'rgba(255, 255, 255, 0.05)';
                      }
                    }}
                    onMouseOut={(e) => {
                      if (activeTab !== tab.key) {
                        e.target.style.background = 'transparent';
                      }
                    }}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Available Auctions Tab */}
              {activeTab === "available" && (
                <div>
                  <div style={{ marginBottom: '16px' }}>
                    <input
                      type="text"
                      placeholder="Search auctions..."
                      value={searchAvailable}
                      onChange={(e) => setSearchAvailable(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        background: 'rgba(255, 255, 255, 0.1)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: '4px',
                        color: 'white',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                  
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                    gap: '12px'
                  }}>
                    {filteredAvailable.map((auction) => (
                      <div key={auction.id} style={{
                        background: 'rgba(255, 255, 255, 0.05)',
                        borderRadius: '8px',
                        padding: '12px',
                        border: '1px solid rgba(255, 255, 255, 0.1)'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                          <img
                            src={`/sprites/${auction.name}.png`}
                            alt={auction.name}
                            width="48"
                            height="48"
                    
                          />
                          <div>
                            <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 'bold' }}>{auction.name}</h4>
                            <p style={{ margin: 0, fontSize: '12px', color: '#ccc' }}>Level {auction.level}</p>
                          </div>
                        </div>
                        
                        <div style={{ fontSize: '12px', marginBottom: '8px' }}>
                          <div>
                            {auction.lastbid > 0 ? (
                              <>Current Bid: <span style={{ color: '#ffe066', display: 'flex', alignItems: 'center', gap: '4px' }}>
                               <img src="/items/coin.png" alt="coins" width="12" height="12"  /> {formatNumber(auction.lastbidAmount)}
                              </span></>
                            ) : (
                              <>Starting Bid: <span style={{ color: '#ffe066', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <img src="/items/coin.png" alt="coins" width="12" height="12"  />{formatNumber(auction.startingbid)}
                              </span></>
                            )}
                          </div>
                          <div>Time Left: <span style={{ color: '#ff6b6b' }}>{getTimeLeft(auction.ends)}</span></div>
                          <div>Trainer: {auction.trainer}</div>
                        </div>
                        
                        <button
                          onClick={() => {
                            setCurrentAuction(auction);
                            setBidModalOpen(true);
                          }}
                          disabled={auction.trainer === authUser || auction.trainer?.toLowerCase() === authUser?.toLowerCase() || isAuctionExpired(auction.ends)}
                          style={{
                            width: '100%',
                            padding: '8px',
                            background: (auction.trainer === authUser || auction.trainer?.toLowerCase() === authUser?.toLowerCase() || isAuctionExpired(auction.ends)) 
                              ? 'rgba(128, 128, 128, 0.5)' 
                              : 'linear-gradient(135deg, #4CAF50, #45a049)',
                            border: 'none',
                            borderRadius: '4px',
                            color: (auction.trainer === authUser || auction.trainer?.toLowerCase() === authUser?.toLowerCase() || isAuctionExpired(auction.ends)) ? '#ccc' : 'white',
                            fontSize: '12px',
                            fontWeight: 'bold',
                            cursor: (auction.trainer === authUser || auction.trainer?.toLowerCase() === authUser?.toLowerCase() || isAuctionExpired(auction.ends)) ? 'not-allowed' : 'pointer',
                            transition: 'all 0.2s ease',
                            opacity: (auction.trainer === authUser || auction.trainer?.toLowerCase() === authUser?.toLowerCase() || isAuctionExpired(auction.ends)) ? 0.6 : 1
                          }}
                          onMouseEnter={(e) => {
                            if (auction.trainer !== authUser && auction.trainer?.toLowerCase() !== authUser?.toLowerCase() && !isAuctionExpired(auction.ends)) {
                              e.target.style.transform = 'translateY(-1px)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (auction.trainer !== authUser && auction.trainer?.toLowerCase() !== authUser?.toLowerCase() && !isAuctionExpired(auction.ends)) {
                              e.target.style.transform = 'translateY(0)';
                            }
                          }}
                        >
                          {isAuctionExpired(auction.ends) ? 'Auction Ended' : (auction.trainer === authUser || auction.trainer?.toLowerCase() === authUser?.toLowerCase()) ? 'Your Auction' : 'Place Bid'}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Hold Auction Tab */}
              {activeTab === "hold" && (
                <div>
                  <h4 style={{ marginBottom: '16px', color: '#ffe066' }}>Create New Auction</h4>
                  
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px' }}>Select Pokémon:</label>
                    <select
                      value={holdAuctionData.pokid}
                      onChange={(e) => setHoldAuctionData({...holdAuctionData, pokid: e.target.value})}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        background: 'rgba(255, 255, 255, 0.1)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: '4px',
                        color: 'white',
                        fontSize: '14px'
                      }}
                    >
                      <option value="">Choose a Pokémon</option>
                      {myPokemon.map((poke) => (
                        <option key={poke.id} value={poke.id} style={{ background: '#1a1a2e' }}>
                          {poke.name} (Level {getLevel(poke.totalexp)})
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '8px', fontSize: '14px' }}>
                    <img src="/items/coin.png" alt="coins" width="12" height="12"  /> Starting Bid:
                    </label>
                    <input
                      type="number"
                      value={holdAuctionData.startingbid}
                      onChange={(e) => setHoldAuctionData({...holdAuctionData, startingbid: e.target.value})}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        background: 'rgba(255, 255, 255, 0.1)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: '4px',
                        color: 'white',
                        fontSize: '14px'
                      }}
                      placeholder={`Enter starting bid (max 50,000)`}
                    />
                  </div>
                  
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px' }}>Duration:</label>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <div style={{ flex: 1 }}>
                        <select
                          value={durationHours}
                          onChange={(e) => {
                            setDurationHours(e.target.value);
                            const totalMinutes = (parseInt(e.target.value) || 0) * 60 + (parseInt(durationMinutes) || 0);
                            setHoldAuctionData({...holdAuctionData, duration: totalMinutes.toString()});
                          }}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            background: 'rgba(255, 255, 255, 0.1)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            borderRadius: '4px',
                            color: 'white',
                            fontSize: '14px'
                          }}
                        >
                          <option value="" style={{ background: '#1a1a2e' }}>Hours</option>
                          {[...Array(25)].map((_, i) => (
                            <option key={i} value={i} style={{ background: '#1a1a2e' }}>
                              {i} {i === 1 ? 'hour' : 'hours'}
                            </option>
                          ))}
                        </select>
                      </div>
                      <span style={{ color: '#ccc', fontSize: '14px' }}>and</span>
                      <div style={{ flex: 1 }}>
                        <select
                          value={durationMinutes}
                          onChange={(e) => {
                            setDurationMinutes(e.target.value);
                            const totalMinutes = (parseInt(durationHours) || 0) * 60 + (parseInt(e.target.value) || 0);
                            setHoldAuctionData({...holdAuctionData, duration: totalMinutes.toString()});
                          }}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            background: 'rgba(255, 255, 255, 0.1)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            borderRadius: '4px',
                            color: 'white',
                            fontSize: '14px'
                          }}
                        >
                          <option value="" style={{ background: '#1a1a2e' }}>Minutes</option>
                          {[0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55].map((minutes) => (
                            <option key={minutes} value={minutes} style={{ background: '#1a1a2e' }}>
                              {minutes} {minutes === 1 ? 'minute' : 'minutes'}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    {(durationHours || durationMinutes) && (
                      <div style={{ marginTop: '4px', fontSize: '12px', color: '#ffe066' }}>
                        Total: {((parseInt(durationHours) || 0) * 60 + (parseInt(durationMinutes) || 0))} minutes
                        {((parseInt(durationHours) || 0) * 60 + (parseInt(durationMinutes) || 0)) < 10 && (
                          <span style={{ color: '#ff6b6b', marginLeft: '8px' }}>⚠️ Minimum 10 minutes required</span>
                        )}
                      </div>
                    )}
                  </div>
                  
                  <button
                    onClick={submitHoldAuction}
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: 'linear-gradient(135deg, #FF6B35, #F7931E)',
                      border: 'none',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
                    onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
                  >
                    Create Auction
                  </button>
                </div>
              )}

              {/* My Held Auctions Tab */}
              {activeTab === "myHeld" && (
                <div>
                  <div style={{ marginBottom: '16px' }}>
                    <input
                      type="text"
                      placeholder="Search my auctions..."
                      value={searchHeld}
                      onChange={(e) => setSearchHeld(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        background: 'rgba(255, 255, 255, 0.1)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: '4px',
                        color: 'white',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                  
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                    gap: '12px'
                  }}>
                    {filteredMyHeld.map((auction) => (
                      <div key={auction.id} style={{
                        background: 'rgba(255, 255, 255, 0.05)',
                        borderRadius: '8px',
                        padding: '12px',
                        border: '1px solid rgba(255, 255, 255, 0.1)'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                          <img
                            src={`/sprites/${auction.name}.png`}
                            alt={auction.name}
                            width="48"
                            height="48"
        
                          />
                          <div>
                            <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 'bold' }}>{auction.name}</h4>
                            <p style={{ margin: 0, fontSize: '12px', color: '#ccc' }}>Level {auction.level}</p>
                          </div>
                        </div>
                        
                        <div style={{ fontSize: '12px' }}>
                          <div>
                            {auction.lastbid > 0 ? (
                              <>Current Bid: <span style={{ color: '#ffe066', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <img src="/items/coin.png" alt="coins" width="12" height="12" /> {formatNumber(auction.lastbidAmount)} 
                              </span></>
                            ) : (
                              <>Starting Bid: <span style={{ color: '#ffe066', display: 'flex', alignItems: 'center', gap: '4px' }}>
                               <img src="/items/coin.png" alt="coins" width="12" height="12" /> {formatNumber(auction.startingbid)}
                              </span></>
                            )}
                          </div>
                          <div>Time Left: <span style={{ color: '#ff6b6b' }}>{getTimeLeft(auction.ends)}</span></div>
                          <div>Last Bidder: {auction.lastbidUsername || "None"}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* My Bids Tab */}
              {activeTab === "myBids" && (
                <div>
                  <div style={{ marginBottom: '16px' }}>
                    <input
                      type="text"
                      placeholder="Search my bids..."
                      value={searchBids}
                      onChange={(e) => setSearchBids(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        background: 'rgba(255, 255, 255, 0.1)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: '4px',
                        color: 'white',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                  
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                    gap: '12px'
                  }}>
                    {filteredMyBids.map((auction) => (
                      <div key={auction.id} style={{
                        background: 'rgba(255, 255, 255, 0.05)',
                        borderRadius: '8px',
                        padding: '12px',
                        border: '1px solid rgba(255, 255, 255, 0.1)'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                          <img
                            src={`/sprites/${auction.name}.png`}
                            alt={auction.name}
                            width="48"
                            height="48"
                
                          />
                          <div>
                            <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 'bold' }}>{auction.name}</h4>
                            <p style={{ margin: 0, fontSize: '12px', color: '#ccc' }}>Level {auction.level}</p>
                          </div>
                        </div>
                        
                        <div style={{ fontSize: '12px' }}>
                          <div>My Bid: <span style={{ color: '#4CAF50', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <img src="/items/coin.png" alt="coins" width="12" height="12"  /> {formatNumber(auction.lastbidAmount)} 
                          </span></div>
                          <div>Time Left: <span style={{ color: '#ff6b6b' }}>{getTimeLeft(auction.ends)}</span></div>
                          <div>Trainer: {auction.trainer}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Bid Modal */}
      {bidModalOpen && currentAuction && (
        <div 
          onClick={() => setBidModalOpen(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1060
          }}>
          <div 
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
              borderRadius: '12px',
              border: '2px solid rgba(255, 255, 255, 0.1)',
              padding: '24px',
              maxWidth: '400px',
              width: '90%',
              color: 'white'
            }}>
            <h3 style={{ marginBottom: '16px', textAlign: 'center' }}>Place Bid</h3>
            
            <div style={{ textAlign: 'center', marginBottom: '16px' }}>
              <img
                src={`/sprites/${currentAuction.name}.png`}
                alt={currentAuction.name}
                width="64"
                height="64"
              />
              <h4 style={{ margin: '8px 0' }}>{currentAuction.name}</h4>
              <p style={{ margin: '4px 0', fontSize: '12px', color: '#aaa' }}>Level {currentAuction.level}</p>
              <p style={{ margin: '8px 0 4px 0', color: '#ccc', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                {currentAuction.lastbidAmount > 0 ? (
                  <>Current Bid:<img src="/items/coin.png" alt="coins" width="14" height="14" /> {formatNumber(currentAuction.lastbidAmount)} </>
                ) : (
                  <>Starting Bid: <img src="/items/coin.png" alt="coins" width="14" height="14" /> {formatNumber(currentAuction.startingbid)}</>
                )}
              </p>
              
              {/* Pokemon Moves */}
              {currentAuction.moves && currentAuction.moves.filter(move => move !== "Empty").length > 0 && (
                <div style={{ 
                  marginTop: '12px', 
                  padding: '8px', 
                  background: 'rgba(255, 255, 255, 0.05)', 
                  borderRadius: '4px',
                  border: '1px solid rgba(255, 255, 255, 0.1)'
                }}>
                  <p style={{ margin: '0 0 6px 0', fontSize: '12px', color: '#ffe066', fontWeight: 'bold' }}>Moves:</p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                    {currentAuction.moves
                      .filter(move => move !== "Empty")
                      .map((move, index) => (
                        <span 
                          key={index}
                          style={{
                            fontSize: '11px',
                            padding: '2px 6px',
                            background: 'rgba(76, 175, 80, 0.2)',
                            color: '#4CAF50',
                            borderRadius: '3px',
                            border: '1px solid rgba(76, 175, 80, 0.3)'
                          }}
                        >
                          {move}
                        </span>
                      ))
                    }
                  </div>
                </div>
              )}
            </div>
            
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '8px' }}>
                Your Bid Amount: <img src="/items/coin.png" alt="coins" width="12" height="12" />
              </label>
              <input
                type="number"
                value={bidAmount}
                onChange={(e) => setBidAmount(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '4px',
                  color: 'white',
                  fontSize: '14px'
                }}
                placeholder={`Minimum: ${(currentAuction.lastbidAmount > 0 ? parseInt(currentAuction.lastbidAmount) : parseInt(currentAuction.startingbid)) + 1}`}
              />
            </div>
            
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => setBidModalOpen(false)}
                style={{
                  flex: 1,
                  padding: '10px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '4px',
                  color: 'white',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={submitBid}
                style={{
                  flex: 1,
                  padding: '10px',
                  background: 'linear-gradient(135deg, #4CAF50, #45a049)',
                  border: 'none',
                  borderRadius: '4px',
                  color: 'white',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
              >
                Place Bid
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuctionHouseModal;
